import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, Building2, User } from "lucide-react";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const STATUSES = ["submitted", "client_review", "interview", "placed", "rejected"];
const STATUS_STYLE = {
  submitted: "text-cyan-300 border-cyan-500/30 bg-cyan-500/10",
  client_review: "text-blue-300 border-blue-500/30 bg-blue-500/10",
  interview: "text-amber-300 border-amber-500/30 bg-amber-500/10",
  placed: "text-teal-300 border-teal-500/30 bg-teal-500/10",
  rejected: "text-red-300 border-red-500/30 bg-red-500/10",
};
const LABEL = { submitted: "Submitted", client_review: "Client review", interview: "Interview", placed: "Placed", rejected: "Rejected" };

export default function Submissions() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); api.get("/agency/submissions").then((r) => setSubs(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    setSubs((prev) => prev.map((s) => (s.id === id ? { ...s, status } : s)));
    try { await api.put(`/agency/submissions/${id}/status`, { status }); toast.success(`Marked ${LABEL[status]}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen wo-bg">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 pb-28 lg:pb-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-teal-500/10 border border-teal-500/20 flex items-center justify-center text-teal-400"><Send size={20} /></div>
          <div>
            <h1 className="font-display font-bold text-3xl text-slate-100">Submissions</h1>
            <p className="text-slate-400 text-sm"><span className="font-mono text-teal-400" data-testid="submissions-count">{subs.length}</span> candidates presented to clients</p>
          </div>
        </div>

        {loading ? (
          <p className="text-slate-500 text-center py-20">Loading…</p>
        ) : subs.length === 0 ? (
          <div className="text-center py-24 text-slate-500" data-testid="submissions-empty"><Send size={40} className="mx-auto text-slate-600 mb-4" /> No submissions yet. Present a candidate to a client from Talent Search.</div>
        ) : (
          <div className="space-y-3">
            {subs.map((s) => (
              <div key={s.id} data-testid="submission-row" className="liquid-glass rounded-xl p-4 flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-teal-400 to-blue-600 flex items-center justify-center text-sm font-bold text-slate-900 shrink-0">{s.candidate_name?.[0]?.toUpperCase()}</div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-100 truncate flex items-center gap-1.5"><User size={13} className="text-slate-500" /> {s.candidate_name}</div>
                    <div className="text-xs text-slate-400 truncate flex items-center gap-1.5"><Building2 size={12} /> {s.client_name}{s.job_title ? ` · ${s.job_title}` : ""}</div>
                    {s.note && <div className="text-xs text-slate-500 mt-0.5 truncate">“{s.note}”</div>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[11px] px-2.5 py-1 rounded-full border ${STATUS_STYLE[s.status]}`}>{LABEL[s.status]}</span>
                  <Select value={s.status} onValueChange={(v) => setStatus(s.id, v)}>
                    <SelectTrigger data-testid="submission-status-select" className="w-40 h-9 text-xs bg-slate-950/50 border-white/10 text-slate-200"><SelectValue /></SelectTrigger>
                    <SelectContent className="text-slate-200">
                      {STATUSES.map((st) => <SelectItem key={st} value={st}>{LABEL[st]}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
